import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { APIS } from "../../../config/apis"
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { MEDIO_PAGO } from "../../../consts/medioPago"
import { ServerRequest } from "../../../utils/apiweb"
import { onRequestError, onRequestNoSuccess } from "../../../utils/requests"
import { FormLabel, Modal, TableCustom, FormError } from "../../common"
import { useForm, useLista, useManagedContext, useFormErrors } from "../../hooks"
import { VALIDATION_MESSAGE } from "../../../consts/validationMessage"
import { getDateNow, getDateToString, getFormatNumber, getNumeroRecibo_ObjectToString, getNumeroRecibo_IdentificadorToObject, precisionRound } from "../../../utils/convert"
import { isValidNumber, isValidString } from "../../../utils/validator"
import CobranzaCajaModal from "../CobranzaCajaModal"
import './index.scss'


const MEDIO_PAGO_MODAL = {
    [MEDIO_PAGO.EFECTIVO]: 'efectivo',
    [MEDIO_PAGO.TARJETA_CREDITO]: 'tarjetaCredito',
    [MEDIO_PAGO.TARJETA_DEBITO]: 'tarjetaDebito',
    [MEDIO_PAGO.TRANSFERENCIA]: 'transferencia',
    [MEDIO_PAGO.CHEQUE]: 'cheque',
    [MEDIO_PAGO.QR]: 'qr'
}

const levenshtein = (a, b) => {
    const m = a.length, n = b.length
    const dp = Array.from({length: m + 1}, (_, i) => i)
    for (let j = 1; j <= n; j++) {
        let prev = dp[0]
        dp[0] = j
        for (let i = 1; i <= m; i++) {
            const temp = dp[i]
            dp[i] = a[i-1] === b[j-1] ? prev : 1 + Math.min(prev, dp[i], dp[i-1])
            prev = temp
        }
    }
    return dp[m]
}

const findClosestMatch = (input, items) => {
    if (!input || items.length === 0) return items[0] || null
    const lower = input.toLowerCase()
    return items.reduce((best, item) => {
        const dist = levenshtein(lower, item.nombre.toLowerCase())
        return dist < best.dist ? { item, dist } : best
    }, { item: items[0], dist: Infinity }).item
}

// Obtener fecha e importe vigentes de un recibo.
// Usa getDateNow(false) para consistencia con FindRecibo.
export const getVencimientoVigente = (recibo) => {
    const hoy = getDateNow(false)
    if (recibo.fechaVencimiento1 && hoy.getTime() <= recibo.fechaVencimiento1.getTime()) {
        return { fecha: recibo.fechaVencimiento1, importe: recibo.importeVencimiento1 }
    }
    if (recibo.fechaVencimiento2 && hoy.getTime() <= recibo.fechaVencimiento2.getTime()) {
        return { fecha: recibo.fechaVencimiento2, importe: recibo.importeVencimiento2 }
    }
    if (recibo.fechaVencimiento2) {
        return { fecha: recibo.fechaVencimiento2, importe: recibo.importeVencimiento2 }
    }
    return { fecha: recibo.fechaVencimiento1, importe: recibo.importeVencimiento1 }
}

const CobranzasModal = ({ cajaResumen, onClose, onCobranzaRealizada }) => {
    const { setIsLoading, showMessageModal } = useManagedContext()

    const inputCodigoBarrasRef = useRef(null);
    const inputEntidadRef = useRef(null);
    const inputNumeroRef = useRef(null);
    const inputImporteRef = useRef(null);
    const medioPagoIdCounter = useRef(0);
    const recibosEnVuelo = useRef(new Set());

    const [recibos, setRecibos] = useState([])
    const [mediosPagos, setMediosPagos] = useState([])
    const [formValues, formHandle, , formSet] = useForm({ codigoBarras: '' })

    const getBarrasErrors = () => {
        const errors = {}
        if (formValues.codigoBarras.length === 0) errors.codigoBarras = VALIDATION_MESSAGE.REQUIRED
        return errors
    }

    const { errorOf, validate, reset } = useFormErrors(getBarrasErrors)

    const [showCobranzaCaja, setShowCobranzaCaja] = useState(false)
    const [showMedioPagoModal, setShowMedioPagoModal] = useState(null) // 'efectivo', 'tarjetaCredito', 'tarjetaDebito', 'transferencia', 'cheque'
    const [medioPagoForm, setMedioPagoForm] = useState({ importe: 0, numero: '', entidad: '', entidadFilter: '' })
    const [showEntidadDropdown, setShowEntidadDropdown] = useState(false)
    const [comboboxIndex, setComboboxIndex] = useState(0)
    const [accordions, setAccordions] = useState({
        veps: true,
        mediosPago: false
    })
    const [bancos, setBancos] = useState([])
    const [emisores, setEmisores] = useState([])
    const [mediosPagoLista, setMediosPagoLista] = useState([])

    // Expandir accordion medios de pago solo la primera vez que se agregan VEPs
    const prevRecibosLength = useRef(0)
    useEffect(() => {
        if (prevRecibosLength.current === 0 && recibos.length >= 1) {
            setAccordions(prev => ({ ...prev, mediosPago: true }))
        }
        prevRecibosLength.current = recibos.length
    }, [recibos.length])

    const totalVeps = useMemo(() => {
        let total = 0;
        for (let r = 0; r < recibos.length; r++) {
            total += getVencimientoVigente(recibos[r]).importe;
        }
        return precisionRound(total);
    }, [recibos])

    const totalMediosPago = useMemo(() => {
        let total = 0;
        for (let mp = 0; mp < mediosPagos.length; mp++) {
            total += mediosPagos[mp].importeCobro;
        }
        return precisionRound(total);
    }, [mediosPagos])

    const canFinalize = totalVeps > 0 && Math.abs(totalVeps - totalMediosPago) < 0.01;

    // ScrollIntoView para combobox cuando cambia el índice seleccionado
    useEffect(() => {
        const selectedOption = document.querySelector('.cobranzas-combobox-option.selected')
        if (selectedOption) {
            selectedOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
    }, [comboboxIndex])

    const [getListLista, getRowLista] = useLista({
        listas: ['MedioPago', 'Banco', 'Emisor'],
        onLoaded: (_, isSuccess, error) => {
            if (isSuccess) {
                setBancos(getListLista('Banco'))
                setEmisores(getListLista('Emisor'))
                setMediosPagoLista(getListLista('MedioPago'))
            } else {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
            }
        },
        memo: {
            key: 'MedioPago_Banco_Emisor',
            timeout: 0
        }
    })

    const mediosPagoOrdenados = useMemo(() => {
        return [...mediosPagoLista].sort((a, b) => a.orden - b.orden)
    }, [mediosPagoLista])

    const FindRecibo = () => {
        if (!validate()) return

        let paramsUrl = "";

        const codigoBarrasCliente = formValues.codigoBarras;
        const hasDash = codigoBarrasCliente.includes('-')
        const normalized = codigoBarrasCliente.replace(/-/g, '')
        if (/^\d+$/.test(normalized) && (hasDash || codigoBarrasCliente.length === 13)) {
            const { codigoDelegacion, numeroRecibo } = getNumeroRecibo_IdentificadorToObject(codigoBarrasCliente)
            paramsUrl = `/recibo/numero/${codigoDelegacion}/${numeroRecibo}`
        }
        else {
            paramsUrl = `/recibo/codigo-barras-cliente/${codigoBarrasCliente}`
        }

        setIsLoading(true)

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.RECIBO_PUBLICACION_LOTE,
            paramsUrl,
            null,
            res => res.json().then(recibo => {
                recibo.fechaVencimiento1 = new Date(recibo.fechaVencimiento1)
                recibo.fechaVencimiento2 = new Date(recibo.fechaVencimiento2)
                const fechaCobro = getDateNow(false)
                if (recibos.filter(f => f.id === recibo.id).length > 0 || recibosEnVuelo.current.has(recibo.id)) {
                    ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'El recibo ya fue ingresado')
                }
                else if (fechaCobro.getTime() > recibo.fechaVencimiento2.getTime()) {
                    ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'El recibo está vencido')
                }
                else {
                    recibosEnVuelo.current.add(recibo.id)
                    setRecibos(prev => [...prev, recibo])
                }
                formSet({ ...formValues, codigoBarras: '' })
                reset()
                setIsLoading(false)
                if (inputCodigoBarrasRef.current) inputCodigoBarrasRef.current.focus()
            }),
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
                if (inputCodigoBarrasRef.current) inputCodigoBarrasRef.current.focus()
            },
            err => {
                onRequestError(err)
                setIsLoading(false)
            },
        )
    }

    const confirmRemoveRecibo = (id) => {
        recibosEnVuelo.current.delete(id)
        setRecibos(prev => prev.filter(x => x.id !== id))
        setTimeout(() => inputCodigoBarrasRef.current?.focus(), 100)
    }
    const confirmRemoveMedioPago = (id) => {
        setMediosPagos(prev => prev.filter(x => x.id !== id))
        setTimeout(() => inputCodigoBarrasRef.current?.focus(), 100)
    }

    const importePendiente = useMemo(() => {
        return precisionRound(totalVeps - totalMediosPago);
    }, [totalVeps, totalMediosPago])

    const openMedioPagoModal = useCallback((tipo) => {
        const importe = tipo === 'efectivo' ? '' : importePendiente
        setMedioPagoForm({ importe, numero: '', entidad: '', entidadFilter: '' })
        setShowEntidadDropdown(false)
        setShowMedioPagoModal(tipo)
    }, [importePendiente])

    const closeMedioPagoModal = () => {
        resetMedioPago()
        setShowMedioPagoModal(null)
        setMedioPagoForm({ importe: 0, numero: '', entidad: '', entidadFilter: '' })
        setShowEntidadDropdown(false)
        setComboboxIndex(0)
        setTimeout(() => inputCodigoBarrasRef.current?.focus(), 100)
    }

    // Convierte número a string con formato argentino (coma decimal)
    const numberToArgString = (num) => {
        if (num === 0 || num === '' || num === null || num === undefined) return ''
        const fixed = Number(num).toFixed(2)
        const [intPart, decPart] = fixed.split('.')
        // Si los decimales son 00, no los mostramos
        return decPart === '00' ? intPart : `${intPart},${decPart}`
    }

    // Formato visual de montos (solo display, no afecta valor)
    const formatMontoDisplay = (value) => {
        if (value === '' || value === null || value === undefined) return ''
        // Si es número, convertir primero a string argentino
        const strValue = typeof value === 'number' ? numberToArgString(value) : String(value)
        const numStr = strValue.replace(/[^\d,]/g, '')
        const parts = numStr.split(',')
        const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
        return parts.length > 1 ? `${intPart},${parts[1].slice(0, 2)}` : intPart
    }

    const handleMontoChange = (value) => {
        // Permitir solo dígitos y una coma
        const cleaned = value.replace(/[^\d,]/g, '').replace(/,/g, (m, i, s) => s.indexOf(',') === i ? m : '')
        setMedioPagoForm(prev => ({...prev, importe: cleaned}))
    }

    const getMontoNumerico = (value) => {
        if (!value) return 0
        if (typeof value === 'number') return value
        const numStr = String(value).replace(/\./g, '').replace(',', '.')
        return parseFloat(numStr) || 0
    }

    const getMedioPagoErrors = () => {
        const errors = {}
        if (!showMedioPagoModal) return errors

        if (!isValidNumber(getMontoNumerico(medioPagoForm.importe), true)) errors.importe = VALIDATION_MESSAGE.REQUIRED

        if (['tarjetaCredito','tarjetaDebito','cheque'].includes(showMedioPagoModal)) {
            if (!isValidString(medioPagoForm.entidad, true)) errors.entidad = VALIDATION_MESSAGE.REQUIRED_OPTION
            if (!isValidString(medioPagoForm.numero, true)) errors.numero = VALIDATION_MESSAGE.REQUIRED
        }

        return errors
    }

    const getMedioPagoFormatErrors = () => {
        const errors = {}
        if (!showMedioPagoModal || showMedioPagoModal === 'efectivo') return errors

        if (getMontoNumerico(medioPagoForm.importe) > importePendiente) errors.importe = 'Supera el importe pendiente'

        return errors
    }

    const {
        errorOf: errorOfMedioPago,
        validate: validateMedioPago,
        reset: resetMedioPago
    } = useFormErrors(getMedioPagoErrors, getMedioPagoFormatErrors)

    // Listas filtradas para combobox
    const filteredEmisores = useMemo(() => {
        return emisores.filter(em => em.nombre.toLowerCase().includes(medioPagoForm.entidadFilter.toLowerCase()))
    }, [emisores, medioPagoForm.entidadFilter])

    const filteredBancos = useMemo(() => {
        return bancos.filter(b => b.nombre.toLowerCase().includes(medioPagoForm.entidadFilter.toLowerCase()))
    }, [bancos, medioPagoForm.entidadFilter])

    // Generar detalle para medio de pago
    const getMedioPagoDetalle = (row) => {
        const { bancoMedioPago, numeroMedioPago } = row
        const parts = []
        if (bancoMedioPago) parts.push(bancoMedioPago)
        if (numeroMedioPago) parts.push(`#${numeroMedioPago}`)
        return parts.join(' - ') || '-'
    }

    // Funciones de submit para cada modal
    const submitTarjetaCredito = () => {
        registrarMedioPago(MEDIO_PAGO.TARJETA_CREDITO, getMontoNumerico(medioPagoForm.importe), medioPagoForm.numero, medioPagoForm.entidad)
    }

    const submitTarjetaDebito = () => {
        registrarMedioPago(MEDIO_PAGO.TARJETA_DEBITO, getMontoNumerico(medioPagoForm.importe), medioPagoForm.numero, medioPagoForm.entidad)
    }

    const submitCheque = () => {
        registrarMedioPago(MEDIO_PAGO.CHEQUE, getMontoNumerico(medioPagoForm.importe), medioPagoForm.numero, medioPagoForm.entidad)
    }

    const registrarMedioPago = (idMedioPago, importe, numero = '', entidad = '') => {
        if (!validateMedioPago()) return

        const medioPago = {
            id: -(++medioPagoIdCounter.current),
            idMedioPago: idMedioPago,
            numeroMedioPago: numero,
            bancoMedioPago: entidad,
            importeCobro: importe > importePendiente ? importePendiente : importe
        }
        setMediosPagos(prev => [...prev, medioPago])
        closeMedioPagoModal()
    }

    const handleCobrarRecibos = useCallback(() => {
        if (recibos.length === 0) {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'Debe ingresar recibos para cobrar primero')
            return
        }
        setShowCobranzaCaja(true)
    }, [recibos.length])

    // Keyboard shortcuts (definido después de openMedioPagoModal y handleCobrarRecibos)
    const handleKeyDown = useCallback((e) => {
        if (showMedioPagoModal || showCobranzaCaja) return;

        if (e.key === 'F10' && canFinalize) {
            e.preventDefault();
            handleCobrarRecibos();
            return;
        }

        if (recibos.length === 0) return;

        const medio = mediosPagoOrdenados.find(x => `F${x.orden}` === e.key);
        if (medio) {
            e.preventDefault();
            openMedioPagoModal(MEDIO_PAGO_MODAL[medio.id]);
        }
    }, [showMedioPagoModal, showCobranzaCaja, recibos.length, canFinalize, mediosPagoOrdenados, openMedioPagoModal, handleCobrarRecibos]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown])

    const avmrRecibo = {
        onRemove: ({ id }) => {
            showMessageModal({
                title: "Confirmación",
                message: "¿Está seguro/a de quitar el recibo?",
                onConfirm: () => confirmRemoveRecibo(id),
            })
        }
    }

    const avmrMedioPago = {
        onRemove: ({ id }) => {
            showMessageModal({
                title: "Confirmación",
                message: "¿Está seguro/a de quitar el medio de pago?",
                onConfirm: () => confirmRemoveMedioPago(id),
            })
        }
    }

    function ToggleAccordion(accordion) {
        if (accordion === 'mediosPago' && recibos.length === 0) {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'Primero debe agregar VEPs')
            return;
        }
        setAccordions(prev => ({
            ...prev,
            [accordion]: !prev[accordion]
        }));
    }

    const accordionClose = <span className="material-symbols-outlined search-i">chevron_right</span>
    const accordionOpen = <span className="material-symbols-outlined search-i">expand_more</span>

    const isMediosPagoDisabled = recibos.length === 0;

    const hasDatos = recibos.length > 0 || mediosPagos.length > 0;

    const handleVolverACaja = () => {
        if (hasDatos) {
            showMessageModal({
                title: 'Confirmar',
                message: '¿Está seguro que desea volver a caja? Se perderán los datos cargados.',
                onConfirm: () => onClose(),
                onCancel: () => {}
            })
        } else {
            onClose()
        }
    }

    return (
        <>
            <Modal
                size="fullscreen"
                onEscape={handleVolverACaja}
                title="Cobranzas"
                className="cobranzas-modal"
                body={(
                    <>
                        {/* Accordion VEPs */}
                        <div className='accordion-header'>
                            <div className='row'>
                                <div className="col-12" onClick={() => ToggleAccordion('veps')}>
                                    <div className='accordion-header-title'>
                                        {(accordions.veps) ? accordionOpen : accordionClose}
                                        <h3 className={accordions.veps ? 'active' : ''}>VEPs</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {(accordions.veps &&
                        <div className='accordion-body'>
                            <div className="row form-basic cobranzas-input-codigo">
                                <div className="col-12">
                                    <FormLabel htmlFor="codigoBarras" required>Código de barras o número de VEP</FormLabel>
                                    <div className="cobranzas-input-codigo-row">
                                        <input
                                            ref={inputCodigoBarrasRef}
                                            name="codigoBarras"
                                            autoComplete="off"
                                            placeholder=""
                                            className={`form-control ${errorOf('codigoBarras') ? 'is-invalid' : ''}`}
                                            value={formValues.codigoBarras}
                                            onChange={formHandle}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    FindRecibo()
                                                }
                                            }}
                                            tabIndex={1}
                                            autoFocus
                                        />
                                        <button className="btn btn-outline-primary" onClick={(event) => FindRecibo()} tabIndex={2}>Agregar</button>
                                    </div>
                                    <FormError message={errorOf('codigoBarras')} />
                                </div>
                            </div>
                            <TableCustom
                                className={'TableCustomBase m-top-10'}
                                data={recibos}
                                avmr={avmrRecibo}
                                columns={[
                                    { Header: 'Cuenta', accessor: 'numeroCuenta', width: '20%' },
                                    { Header: 'Número', Cell: ({ row }) => getNumeroRecibo_ObjectToString(row.original), accessor: 'numeroRecibo', width: '20%' },
                                    { Header: 'Periodo', accessor: 'periodo', width: '12%' },
                                    { Header: 'Cuota', accessor: 'cuota', width: '10%' },
                                    { Header: 'Fecha Vto.', Cell: ({ row }) => getDateToString(getVencimientoVigente(row.original).fecha, false), accessor: 'fechaVencimiento1', width: '18%' },
                                    { Header: 'Importe', Cell: ({ row }) => getFormatNumber(getVencimientoVigente(row.original).importe, 2), accessor: 'importeVencimiento1', width: '20%', alignCell: 'right' },
                                ]}
                                showDownloadCSV={false}
                                showFilterGlobal={false}
                            />
                        </div>
                        )}

                        {/* Accordion Medios de Pago */}
                        <div className={`accordion-header m-top-10 ${isMediosPagoDisabled ? 'accordion-disabled' : ''}`}>
                            <div className='row'>
                                <div className="col-12" onClick={() => ToggleAccordion('mediosPago')}>
                                    <div className='accordion-header-title'>
                                        {(accordions.mediosPago && !isMediosPagoDisabled) ? accordionOpen : accordionClose}
                                        <h3 className={accordions.mediosPago && !isMediosPagoDisabled ? 'active' : ''}>Medios de pago</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {(accordions.mediosPago && !isMediosPagoDisabled &&
                        <div className='accordion-body'>
                            <div className="cobranzas-medios-pago-grid">
                                {mediosPagoOrdenados.map((medio, index) => (
                                    <button key={medio.id} className="cobranzas-medio-pago-btn" onClick={() => openMedioPagoModal(MEDIO_PAGO_MODAL[medio.id])} tabIndex={index === 0 ? 3 : undefined}>
                                        <span>{medio.nombre}</span>
                                        <span className="cobranzas-kbd">{`F${medio.orden}`}</span>
                                    </button>
                                ))}
                            </div>
                            <TableCustom
                                className={'TableCustomBase m-top-10'}
                                data={mediosPagos}
                                avmr={avmrMedioPago}
                                columns={[
                                    { Header: 'Medio de pago', accessor: 'idMedioPago', width: '30%', Cell: ({ value }) => getRowLista('MedioPago', value)?.nombre ?? '' },
                                    { Header: 'Detalle', accessor: 'bancoMedioPago', width: '40%', Cell: ({ row }) => getMedioPagoDetalle(row.original) },
                                    { Header: 'Importe', Cell: (data) => getFormatNumber(data.value, 2), accessor: 'importeCobro', width: '30%', alignCell: 'right' }
                                ]}
                                showDownloadCSV={false}
                                showFilterGlobal={false}
                            />
                        </div>
                        )}
                    </>
                )}
                footer={(
                    <div className="cobranzas-footer">
                        <div className="cobranzas-footer-left">
                            <button className="btn back-button" onClick={handleVolverACaja}>Volver a caja</button>
                        </div>
                        <div className="cobranzas-footer-center">
                            <div className="cobranzas-footer-totales">
                                <div className="cobranzas-footer-total">
                                    <span className="cobranzas-footer-total-label">VEPs</span>
                                    <span className="cobranzas-footer-total-value">$ {getFormatNumber(totalVeps, 2)}</span>
                                </div>
                                <div className="cobranzas-footer-total">
                                    <span className="cobranzas-footer-total-label">Pagado</span>
                                    <span className="cobranzas-footer-total-value">$ {getFormatNumber(totalMediosPago, 2)}</span>
                                </div>
                                <div className={`cobranzas-footer-total ${importePendiente === 0 && totalVeps > 0 ? 'cobranzas-footer-total-ok' : ''}`}>
                                    <span className="cobranzas-footer-total-label">Pendiente</span>
                                    <span className="cobranzas-footer-total-value">$ {getFormatNumber(importePendiente, 2)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="cobranzas-footer-right">
                            <button className="btn action-button cobranzas-btn-finalizar" onClick={(event) => handleCobrarRecibos()} tabIndex={4} disabled={!canFinalize}>
                                <span>Finalizar transacción</span>
                                <span className="cobranzas-kbd">F10</span>
                            </button>
                        </div>
                    </div>
                )}
            />

            {/* Modal Efectivo */}
            {(showMedioPagoModal === 'efectivo' &&
                <Modal
                    size="md"
                    className="cobranzas-medio-pago-modal"
                    onEscape={closeMedioPagoModal}
                    title="Pago con efectivo"
                    body={(
                        <>
                            <div className='row form-basic'>
                                <div className="col-12">
                                    <label htmlFor="montoPagar" className="form-label">Monto a pagar</label>
                                    <input
                                        type="text"
                                        name="montoPagar"
                                        className="form-control"
                                        value={getFormatNumber(importePendiente, 2)}
                                        disabled
                                    />
                                </div>
                            </div>
                            <div className='row form-basic'>
                                <div className="col-12">
                                    <FormLabel htmlFor="importeEfectivo" required>Monto recibido</FormLabel>
                                    <input
                                        type="text"
                                        name="importeEfectivo"
                                        placeholder=""
                                        className={`form-control ${errorOfMedioPago('importe') ? 'is-invalid' : ''}`}
                                        value={formatMontoDisplay(medioPagoForm.importe)}
                                        onChange={({target}) => handleMontoChange(target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                registrarMedioPago(MEDIO_PAGO.EFECTIVO, getMontoNumerico(medioPagoForm.importe))
                                            } else if (event.key === 'Escape') {
                                                closeMedioPagoModal()
                                            }
                                        }}
                                        autoFocus
                                    />
                                    <FormError message={errorOfMedioPago('importe')} />
                                </div>
                            </div>
                            <div className='row form-basic'>
                                <div className="col-12">
                                    <label htmlFor="vuelto" className="form-label">Vuelto</label>
                                    <input
                                        type="text"
                                        name="vuelto"
                                        className="form-control"
                                        value={getFormatNumber(Math.max(0, getMontoNumerico(medioPagoForm.importe) - importePendiente), 2)}
                                        disabled
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    footer={(
                        <div className="width-100p disp-b">
                            <button className="btn back-button m-left-5" onClick={closeMedioPagoModal}>Cancelar</button>
                            <button className="btn action-button" onClick={() => registrarMedioPago(MEDIO_PAGO.EFECTIVO, getMontoNumerico(medioPagoForm.importe))}>Registrar</button>
                        </div>
                    )}
                />
            )}

            {/* Modal Tarjeta de Crédito */}
            {showMedioPagoModal === 'tarjetaCredito' &&
                <Modal
                    size="md"
                    className="cobranzas-medio-pago-modal"
                    onEscape={() => showEntidadDropdown ? setShowEntidadDropdown(false) : closeMedioPagoModal()}
                    title="Pago con tarjeta de crédito"
                    body={(
                        <>
                            <div className='row form-basic'>
                                <div className="col-12">
                                    <FormLabel htmlFor="emisor" required>Emisor</FormLabel>
                                    <div className="cobranzas-combobox">
                                        <input
                                            ref={inputEntidadRef}
                                            type="text"
                                            name="emisor"
                                            autoComplete="off"
                                            placeholder="Escriba para buscar..."
                                            className={`form-control ${errorOfMedioPago('entidad') ? 'is-invalid' : ''}`}
                                            value={medioPagoForm.entidadFilter}
                                            onChange={({target}) => {
                                                setMedioPagoForm(prev => ({...prev, entidadFilter: target.value, entidad: ''}))
                                                setShowEntidadDropdown(true)
                                                setComboboxIndex(0)
                                            }}
                                            onFocus={() => {
                                                setMedioPagoForm(prev => ({...prev, entidadFilter: '', entidad: ''}))
                                                setShowEntidadDropdown(true)
                                                setComboboxIndex(0)
                                            }}
                                            onBlur={() => setTimeout(() => {
                                                setShowEntidadDropdown(false)
                                                setMedioPagoForm(prev => {
                                                    if (prev.entidad) return prev
                                                    const fallback = filteredEmisores.length > 0
                                                        ? (filteredEmisores[comboboxIndex] || filteredEmisores[0])
                                                        : findClosestMatch(prev.entidadFilter, emisores)
                                                    if (!fallback) return prev
                                                    return {...prev, entidad: fallback.nombre, entidadFilter: fallback.nombre}
                                                })
                                            }, 200)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'ArrowDown') {
                                                    e.preventDefault()
                                                    setComboboxIndex(prev => Math.min(prev + 1, filteredEmisores.length - 1))
                                                } else if (e.key === 'ArrowUp') {
                                                    e.preventDefault()
                                                    setComboboxIndex(prev => Math.max(prev - 1, 0))
                                                } else if (e.key === 'Enter' && filteredEmisores.length > 0) {
                                                    e.preventDefault()
                                                    const selected = filteredEmisores[comboboxIndex] || filteredEmisores[0]
                                                    setMedioPagoForm(prev => ({...prev, entidad: selected.nombre, entidadFilter: selected.nombre}))
                                                    setShowEntidadDropdown(false)
                                                    setTimeout(() => inputNumeroRef.current?.focus(), 50)
                                                } else if (e.key === 'Escape') {
                                                    if (showEntidadDropdown) {
                                                        setShowEntidadDropdown(false)
                                                    } else {
                                                        closeMedioPagoModal()
                                                    }
                                                }
                                            }}
                                            autoFocus
                                        />
                                        {showEntidadDropdown && filteredEmisores.length > 0 && (
                                            <div className="cobranzas-combobox-dropdown">
                                                {filteredEmisores.map((emisor, idx) => (
                                                    <div
                                                        key={emisor.id}
                                                        className={`cobranzas-combobox-option ${idx === comboboxIndex ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setMedioPagoForm(prev => ({...prev, entidad: emisor.nombre, entidadFilter: emisor.nombre}))
                                                            setShowEntidadDropdown(false)
                                                            setTimeout(() => inputNumeroRef.current?.focus(), 50)
                                                        }}
                                                    >
                                                        {emisor.nombre}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <FormError message={errorOfMedioPago('entidad')} />
                                </div>
                            </div>
                            <div className='row form-basic'>
                                <div className="col-12">
                                    <FormLabel htmlFor="numeroControl" required>Número de control</FormLabel>
                                    <input
                                        ref={inputNumeroRef}
                                        type="text"
                                        name="numeroControl"
                                        placeholder=""
                                        className={`form-control ${errorOfMedioPago('numero') ? 'is-invalid' : ''}`}
                                        value={medioPagoForm.numero}
                                        onChange={({target}) => setMedioPagoForm(prev => ({...prev, numero: target.value}))}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') { e.preventDefault(); submitTarjetaCredito() }
                                            else if (e.key === 'Escape') { closeMedioPagoModal() }
                                        }}
                                        maxLength={50}
                                    />
                                    <FormError message={errorOfMedioPago('numero')} />
                                </div>
                            </div>
                            <div className='row form-basic'>
                                <div className="col-12">
                                    <FormLabel htmlFor="importeTarjetaCredito" required>Monto</FormLabel>
                                    <input
                                        ref={inputImporteRef}
                                        type="text"
                                        name="importeTarjetaCredito"
                                        placeholder=""
                                        className={`form-control ${errorOfMedioPago('importe') ? 'is-invalid' : ''}`}
                                        value={formatMontoDisplay(medioPagoForm.importe)}
                                        onChange={({target}) => handleMontoChange(target.value)}
                                        onKeyDown={(e) => { 
                                            if (e.key === 'Enter') { e.preventDefault(); submitTarjetaCredito() }
                                            else if (e.key === 'Escape') { closeMedioPagoModal() }
                                        }}
                                    />
                                    <FormError message={errorOfMedioPago('importe')} />
                                </div>
                            </div>
                        </>
                    )}
                    footer={(
                        <div className="width-100p disp-b">
                            <button className="btn back-button m-left-5" onClick={closeMedioPagoModal}>Cancelar</button>
                            <button className="btn action-button" onClick={submitTarjetaCredito}>Registrar</button>
                        </div>
                    )}
                />
            }

            {/* Modal Tarjeta de Débito */}
            {showMedioPagoModal === 'tarjetaDebito' &&
                <Modal
                    size="md"
                    className="cobranzas-medio-pago-modal"
                    onEscape={() => showEntidadDropdown ? setShowEntidadDropdown(false) : closeMedioPagoModal()}
                    title="Pago con tarjeta de débito"
                    body={(
                        <>
                            <div className='row form-basic'>
                                <div className="col-12">
                                    <FormLabel htmlFor="emisor" required>Emisor</FormLabel>
                                    <div className="cobranzas-combobox">
                                        <input
                                            ref={inputEntidadRef}
                                            type="text"
                                            name="emisor"
                                            autoComplete="off"
                                            placeholder="Escriba para buscar..."
                                            className={`form-control ${errorOfMedioPago('entidad') ? 'is-invalid' : ''}`}
                                            value={medioPagoForm.entidadFilter}
                                            onChange={({target}) => {
                                                setMedioPagoForm(prev => ({...prev, entidadFilter: target.value, entidad: ''}))
                                                setShowEntidadDropdown(true)
                                                setComboboxIndex(0)
                                            }}
                                            onFocus={() => {
                                                setMedioPagoForm(prev => ({...prev, entidadFilter: '', entidad: ''}))
                                                setShowEntidadDropdown(true)
                                                setComboboxIndex(0)
                                            }}
                                            onBlur={() => setTimeout(() => {
                                                setShowEntidadDropdown(false)
                                                setMedioPagoForm(prev => {
                                                    if (prev.entidad) return prev
                                                    const fallback = filteredEmisores.length > 0
                                                        ? (filteredEmisores[comboboxIndex] || filteredEmisores[0])
                                                        : findClosestMatch(prev.entidadFilter, emisores)
                                                    if (!fallback) return prev
                                                    return {...prev, entidad: fallback.nombre, entidadFilter: fallback.nombre}
                                                })
                                            }, 200)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'ArrowDown') {
                                                    e.preventDefault()
                                                    setComboboxIndex(prev => Math.min(prev + 1, filteredEmisores.length - 1))
                                                } else if (e.key === 'ArrowUp') {
                                                    e.preventDefault()
                                                    setComboboxIndex(prev => Math.max(prev - 1, 0))
                                                } else if (e.key === 'Enter' && filteredEmisores.length > 0) {
                                                    e.preventDefault()
                                                    const selected = filteredEmisores[comboboxIndex] || filteredEmisores[0]
                                                    setMedioPagoForm(prev => ({...prev, entidad: selected.nombre, entidadFilter: selected.nombre}))
                                                    setShowEntidadDropdown(false)
                                                    setTimeout(() => inputNumeroRef.current?.focus(), 50)
                                                } else if (e.key === 'Escape') {
                                                    if (showEntidadDropdown) {
                                                        setShowEntidadDropdown(false)
                                                    } else {
                                                        closeMedioPagoModal()
                                                    }
                                                }
                                            }}
                                            autoFocus
                                        />
                                        {showEntidadDropdown && filteredEmisores.length > 0 && (
                                            <div className="cobranzas-combobox-dropdown">
                                                {filteredEmisores.map((emisor, idx) => (
                                                    <div
                                                        key={emisor.id}
                                                        className={`cobranzas-combobox-option ${idx === comboboxIndex ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setMedioPagoForm(prev => ({...prev, entidad: emisor.nombre, entidadFilter: emisor.nombre}))
                                                            setShowEntidadDropdown(false)
                                                            setTimeout(() => inputNumeroRef.current?.focus(), 50)
                                                        }}
                                                    >
                                                        {emisor.nombre}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <FormError message={errorOfMedioPago('entidad')} />
                                </div>
                            </div>
                            <div className='row form-basic'>
                                <div className="col-12">
                                    <FormLabel htmlFor="numeroControl" required>Número de control</FormLabel>
                                    <input
                                        ref={inputNumeroRef}
                                        type="text"
                                        name="numeroControl"
                                        placeholder=""
                                        className={`form-control ${errorOfMedioPago('numero') ? 'is-invalid' : ''}`}
                                        value={medioPagoForm.numero}
                                        onChange={({target}) => setMedioPagoForm(prev => ({...prev, numero: target.value}))}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') { e.preventDefault(); submitTarjetaDebito() }
                                            else if (e.key === 'Escape') { closeMedioPagoModal() }
                                        }}
                                        maxLength={50}
                                    />
                                    <FormError message={errorOfMedioPago('numero')} />
                                </div>
                            </div>
                            <div className='row form-basic'>
                                <div className="col-12">
                                    <FormLabel htmlFor="importeTarjetaDebito" required>Monto</FormLabel>
                                    <input
                                        ref={inputImporteRef}
                                        type="text"
                                        name="importeTarjetaDebito"
                                        placeholder=""
                                        className={`form-control ${errorOfMedioPago('importe') ? 'is-invalid' : ''}`}
                                        value={formatMontoDisplay(medioPagoForm.importe)}
                                        onChange={({target}) => handleMontoChange(target.value)}
                                        onKeyDown={(e) => { 
                                            if (e.key === 'Enter') { e.preventDefault(); submitTarjetaDebito() }
                                            else if (e.key === 'Escape') { closeMedioPagoModal() }
                                        }}
                                    />
                                    <FormError message={errorOfMedioPago('importe')} />
                                </div>
                            </div>
                        </>
                    )}
                    footer={(
                        <div className="width-100p disp-b">
                            <button className="btn back-button m-left-5" onClick={closeMedioPagoModal}>Cancelar</button>
                            <button className="btn action-button" onClick={submitTarjetaDebito}>Registrar</button>
                        </div>
                    )}
                />
            }

            {/* Modal Transferencia */}
            {showMedioPagoModal === 'transferencia' &&
                <Modal
                    size="md"
                    className="cobranzas-medio-pago-modal"
                    onEscape={closeMedioPagoModal}
                    title="Pago con transferencia"
                    body={(
                        <div className='row form-basic'>
                            <div className="col-12">
                                <FormLabel htmlFor="importeTransferencia" required>Monto transferido</FormLabel>
                                <input
                                    type="text"
                                    name="importeTransferencia"
                                    placeholder=""
                                    className={`form-control ${errorOfMedioPago('importe') ? 'is-invalid' : ''}`}
                                    value={formatMontoDisplay(medioPagoForm.importe)}
                                    onChange={({target}) => handleMontoChange(target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            registrarMedioPago(MEDIO_PAGO.TRANSFERENCIA, getMontoNumerico(medioPagoForm.importe))
                                        } else if (event.key === 'Escape') {
                                            closeMedioPagoModal()
                                        }
                                    }}
                                    autoFocus
                                />
                                <FormError message={errorOfMedioPago('importe')} />
                            </div>
                        </div>
                    )}
                    footer={(
                        <div className="width-100p disp-b">
                            <button className="btn back-button m-left-5" onClick={closeMedioPagoModal}>Cancelar</button>
                            <button className="btn action-button" onClick={() => registrarMedioPago(MEDIO_PAGO.TRANSFERENCIA, getMontoNumerico(medioPagoForm.importe))}>Registrar</button>
                        </div>
                    )}
                />
            }

            {/* Modal QR */}
            {showMedioPagoModal === 'qr' &&
                <Modal
                    size="md"
                    className="cobranzas-medio-pago-modal"
                    onEscape={closeMedioPagoModal}
                    title="Pago con QR"
                    body={(
                        <div className='row form-basic'>
                            <div className="col-12">
                                <FormLabel htmlFor="importeQR" required>Monto abonado</FormLabel>
                                <input
                                    type="text"
                                    name="importeQR"
                                    placeholder=""
                                    className={`form-control ${errorOfMedioPago('importe') ? 'is-invalid' : ''}`}
                                    value={formatMontoDisplay(medioPagoForm.importe)}
                                    onChange={({target}) => handleMontoChange(target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            registrarMedioPago(MEDIO_PAGO.QR, getMontoNumerico(medioPagoForm.importe))
                                        } else if (event.key === 'Escape') {
                                            closeMedioPagoModal()
                                        }
                                    }}
                                    autoFocus
                                />
                                <FormError message={errorOfMedioPago('importe')} />
                            </div>
                        </div>
                    )}
                    footer={(
                        <div className="width-100p disp-b">
                            <button className="btn back-button m-left-5" onClick={closeMedioPagoModal}>Cancelar</button>
                            <button className="btn action-button" onClick={() => registrarMedioPago(MEDIO_PAGO.QR, getMontoNumerico(medioPagoForm.importe))}>Registrar</button>
                        </div>
                    )}
                />
            }

            {/* Modal Cheque */}
            {showMedioPagoModal === 'cheque' &&
                <Modal
                    size="md"
                    className="cobranzas-medio-pago-modal"
                    onEscape={() => showEntidadDropdown ? setShowEntidadDropdown(false) : closeMedioPagoModal()}
                    title="Pago con cheque"
                    body={(
                        <>
                            <div className='row form-basic'>
                                <div className="col-12">
                                    <FormLabel htmlFor="banco" required>Banco</FormLabel>
                                    <div className="cobranzas-combobox">
                                        <input
                                            ref={inputEntidadRef}
                                            type="text"
                                            name="banco"
                                            autoComplete="off"
                                            placeholder="Escriba para buscar..."
                                            className={`form-control ${errorOfMedioPago('entidad') ? 'is-invalid' : ''}`}
                                            value={medioPagoForm.entidadFilter}
                                            onChange={({target}) => {
                                                setMedioPagoForm(prev => ({...prev, entidadFilter: target.value, entidad: ''}))
                                                setShowEntidadDropdown(true)
                                                setComboboxIndex(0)
                                            }}
                                            onFocus={() => {
                                                setMedioPagoForm(prev => ({...prev, entidadFilter: '', entidad: ''}))
                                                setShowEntidadDropdown(true)
                                                setComboboxIndex(0)
                                            }}
                                            onBlur={() => setTimeout(() => {
                                                setShowEntidadDropdown(false)
                                                setMedioPagoForm(prev => {
                                                    if (prev.entidad) return prev
                                                    const fallback = filteredBancos.length > 0
                                                        ? (filteredBancos[comboboxIndex] || filteredBancos[0])
                                                        : findClosestMatch(prev.entidadFilter, bancos)
                                                    if (!fallback) return prev
                                                    return {...prev, entidad: fallback.nombre, entidadFilter: fallback.nombre}
                                                })
                                            }, 200)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'ArrowDown') {
                                                    e.preventDefault()
                                                    setComboboxIndex(prev => Math.min(prev + 1, filteredBancos.length - 1))
                                                } else if (e.key === 'ArrowUp') {
                                                    e.preventDefault()
                                                    setComboboxIndex(prev => Math.max(prev - 1, 0))
                                                } else if (e.key === 'Enter' && filteredBancos.length > 0) {
                                                    e.preventDefault()
                                                    const selected = filteredBancos[comboboxIndex] || filteredBancos[0]
                                                    setMedioPagoForm(prev => ({...prev, entidad: selected.nombre, entidadFilter: selected.nombre}))
                                                    setShowEntidadDropdown(false)
                                                    setTimeout(() => inputNumeroRef.current?.focus(), 50)
                                                } else if (e.key === 'Escape') {
                                                    if (showEntidadDropdown) {
                                                        setShowEntidadDropdown(false)
                                                    } else {
                                                        closeMedioPagoModal()
                                                    }
                                                }
                                            }}
                                            autoFocus
                                        />
                                        {showEntidadDropdown && filteredBancos.length > 0 && (
                                            <div className="cobranzas-combobox-dropdown">
                                                {filteredBancos.map((banco, idx) => (
                                                    <div 
                                                        key={banco.id} 
                                                        className={`cobranzas-combobox-option ${idx === comboboxIndex ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setMedioPagoForm(prev => ({...prev, entidad: banco.nombre, entidadFilter: banco.nombre}))
                                                            setShowEntidadDropdown(false)
                                                            setTimeout(() => inputNumeroRef.current?.focus(), 50)
                                                        }}
                                                    >
                                                        {banco.nombre}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <FormError message={errorOfMedioPago('entidad')} />
                                </div>
                            </div>
                            <div className='row form-basic'>
                                <div className="col-12">
                                    <FormLabel htmlFor="numeroCheque" required>Número de cheque</FormLabel>
                                    <input
                                        ref={inputNumeroRef}
                                        type="text"
                                        name="numeroCheque"
                                        placeholder=""
                                        className={`form-control ${errorOfMedioPago('numero') ? 'is-invalid' : ''}`}
                                        value={medioPagoForm.numero}
                                        onChange={({target}) => setMedioPagoForm(prev => ({...prev, numero: target.value}))}
                                        onKeyDown={(e) => { 
                                            if (e.key === 'Enter') { e.preventDefault(); submitCheque() }
                                            else if (e.key === 'Escape') { closeMedioPagoModal() }
                                        }}
                                        maxLength={50}
                                    />
                                    <FormError message={errorOfMedioPago('numero')} />
                                </div>
                            </div>
                            <div className='row form-basic'>
                                <div className="col-12">
                                    <FormLabel htmlFor="importeCheque" required>Monto</FormLabel>
                                    <input
                                        ref={inputImporteRef}
                                        type="text"
                                        name="importeCheque"
                                        placeholder=""
                                        className={`form-control ${errorOfMedioPago('importe') ? 'is-invalid' : ''}`}
                                        value={formatMontoDisplay(medioPagoForm.importe)}
                                        onChange={({target}) => handleMontoChange(target.value)}
                                        onKeyDown={(e) => { 
                                            if (e.key === 'Enter') { e.preventDefault(); submitCheque() }
                                            else if (e.key === 'Escape') { closeMedioPagoModal() }
                                        }}
                                    />
                                    <FormError message={errorOfMedioPago('importe')} />
                                </div>
                            </div>
                        </>
                    )}
                    footer={(
                        <div className="width-100p disp-b">
                            <button className="btn back-button m-left-5" onClick={closeMedioPagoModal}>Cancelar</button>
                            <button className="btn action-button" onClick={submitCheque}>Registrar</button>
                        </div>
                    )}
                />
            }

            {showCobranzaCaja &&
                <CobranzaCajaModal
                    recibos={recibos}
                    mediosPagos={mediosPagos}
                    cajaResumen={cajaResumen}
                    onConfirm={() => {
                        setShowCobranzaCaja(false)
                        recibosEnVuelo.current.clear()
                        setRecibos([])
                        setMediosPagos([])
                        onCobranzaRealizada()
                        setTimeout(() => inputCodigoBarrasRef.current?.focus(), 100)
                    }}
                    onCancel={() => {
                        setShowCobranzaCaja(false)
                        setTimeout(() => inputCodigoBarrasRef.current?.focus(), 100)
                    }}
                />
            }
        </>
    )
}

export default CobranzasModal
